import {
  ArkosGatewayErrorHandler,
  ArkosGatewayPipe,
  ArkosSocket,
} from "../types";
import { isProduction } from "../../../utils/helpers/arkos-config.helpers";
import { getHandledError } from "../../../modules/error-handler/utils/get-handled-error";

export async function handleArkosGatewayErrors(
  err: any,
  socket: ArkosSocket,
  errorHandlers: ArkosGatewayErrorHandler[] = [],
  loggerMeta: {
    namespace: string;
    event: string;
    startTime: number;
  },
  ack?: (response: any) => void
) {
  let emitCalled = false;
  const originalEmit: any = socket.emit.bind(socket);

  socket.emit = (...args: any[]) => {
    emitCalled = true;
    return originalEmit(...args);
  };

  if (errorHandlers.length) {
    try {
      for (const handler of errorHandlers) await handler(err, socket);
    } catch (catchedErr: any) {
      err = catchedErr;
      emitCalled = false;
    }
  }

  socket.emit = originalEmit;

  if (!emitCalled) {
    console.error("[\x1b[31mError\x1b[0m]:", err);
    err = getHandledError(err);

    const payload = {
      message: err?.message ?? "Internal server error",
      ...(err.isOperational
        ? err
        : { code: "InternalServerError", statusCode: 500 }),
    };

    if (isProduction()) sendProductionError(err, socket);
    else sendDevelopmentError(payload, socket);

    ack?.({ error: payload.message, ...(payload.meta || {}) });

    handleGatewayEventLog(
      loggerMeta.namespace,
      loggerMeta.event,
      payload.statusCode,
      loggerMeta.startTime
    );
  }
}

function sendDevelopmentError(err: any, socket: ArkosSocket): void {
  socket.emit("error", {
    ...err,
    message: err.message?.split?.("\n")[err.message?.split?.("\n").length - 1],
    stack: err?.originalError?.stack?.split?.("\n"),
  });
}

function sendProductionError(err: any, socket: ArkosSocket): void {
  if (err.isOperational)
    socket.emit("event", {
      status: err.status,
      message: err.message,
      meta: err.meta || {},
      code: err.code || "Unknown",
      statusCode: err.statusCode || 500,
    });
  else
    socket.emit("error", {
      status: "error",
      message: "Internal server error, please try again later.",
      code: "InternalServerError",
      meta: {},
      statusCode: 500,
    });
}

export async function runArkosGatewayPipes(
  pipes: ArkosGatewayPipe[],
  socket: ArkosSocket,
  data: any
) {
  for (const pipe of pipes) {
    await pipe(socket, data);
  }
}

export function handleGatewayEventLog(
  namespace: string,
  event: string,
  statusCode: number,
  startTime: number
) {
  const getStatusColor = (statusCode: number) => {
    if (statusCode >= 100 && statusCode < 200) return "\x1b[36m";
    if (statusCode >= 200 && statusCode < 300) return "\x1b[32m";
    if (statusCode >= 300 && statusCode < 400) return "\x1b[33m";
    if (statusCode >= 400 && statusCode < 500) return "\x1b[33m";
    if (statusCode >= 500) return "\x1b[31m";
    return "\x1b[0m";
  };

  const duration = Date.now() - startTime;
  const now = new Date();
  const date = now.toISOString().split("T")[0];
  const time = now.toTimeString().split(" ")[0];
  const timestamp = isProduction() ? `${date} ${time}` : time;
  const statusColor = getStatusColor(statusCode);

  console.info(
    `[\x1b[36mInfo\x1b[0m] \x1b[90m${timestamp}\x1b[0m \x1b[35mWS\x1b[0m ${namespace} ${event} ${statusColor}${statusCode}\x1b[0m \x1b[35m${duration}ms\x1b[0m`
  );
}

export function handleGatewayLifecycleLog(
  namespace: string,
  event: "connected" | "disconnected" | "error",
  socketId: string,
  errorType?: string
) {
  const isProduction = process.env.ARKOS_BUILD == "true";
  const now = new Date();
  const date = now.toISOString().split("T")[0];
  const time = now.toTimeString().split(" ")[0];
  const timestamp = isProduction ? `${date} ${time}` : time;

  const eventColors = {
    connected: "\x1b[32m",
    disconnected: "\x1b[33m",
    error: "\x1b[31m",
  };

  const eventColor = eventColors[event];
  const extra = errorType ? ` ${errorType}` : "";

  console.info(
    `[\x1b[36mInfo\x1b[0m] \x1b[90m${timestamp}\x1b[0m \x1b[35mWS\x1b[0m ${namespace} ${eventColor}${event}\x1b[0m ${socketId}${extra}`
  );
}
